// src/components/Chat.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import styles from './Chat.module.css';
import { sendChatMessage, getChatMessages, submitFeedback, deleteChatSession, getLibraries } from '../api';
import ConfirmationModal from './ConfirmationModal';
import { useNotification } from '../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faThumbsUp,
  faThumbsDown,
  faCopy,
  faImage, // ADICIONE ESTE
  faShareNodes,
  faPenToSquare,
  faArrowRotateRight,
  faCheck,
  faXmark,
  faStop,
  faFileLines,
  faFilePdf,
  faBook
} from '@fortawesome/free-solid-svg-icons';
import { handleShareAsTxt, handleShareAsPdf, convertMarkdownToPlainText } from './ShareComponent';
import FeedbackModal from './FeedbackModal';
import ConversationNavBar from './ConversationNavBar';
import LibrarySelectionModal from './LibrarySelectionModal';

function Chat({
  isSidebarOpen,
  sidebarWidth,
  sessions,
  activeChatId,
  onSelectChat,
  onNewChat,
  onNewChatCreated,
  onTitleUpdate,
  refreshSessions,
  onSessionAnimationEnd
}) {
  const { t, i18n } = useTranslation();
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [oldestSessionForDeletion, setOldestSessionForDeletion] = useState(null);
  const [messageToRetry, setMessageToRetry] = useState('');
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isNewConversation, setIsNewConversation] = useState(false);
  const [isClinicalReasoningActive, setIsClinicalReasoningActive] = useState(false);
  const [textFile, setTextFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [showFileOptions, setShowFileOptions] = useState(false);
  const { addNotification } = useNotification();
  const textareaRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const dropdownRef = useRef(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState({ type: '', content: '', id: null });
  const [shareMenu, setShareMenu] = useState({ open: false, messageId: null });
  const abortControllerRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const shareMenuRef = useRef(null);
  const [actionSuccess, setActionSuccess] = useState({ id: null, type: null });
  const [sessionToRefresh, setSessionToRefresh] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [selectedLibrary, setSelectedLibrary] = useState(null);

  useEffect(() => {
    const fetchLibraries = async () => {
      try {
        const userLibraries = await getLibraries();
        setLibraries(userLibraries);
      } catch (error) {
        console.error("Failed to fetch libraries:", error);
      }
    };
    fetchLibraries();
  }, []);

  useEffect(() => {
    if (!sessionToRefresh || !chatHistory.length) return;

    const lastMessage = chatHistory[chatHistory.length - 1];
    
    if (
      lastMessage.sender === 'bot' &&
      !lastMessage.isLoading &&
      lastMessage.fullBotResponse &&
      lastMessage.text.length >= lastMessage.fullBotResponse.length &&
      lastMessage.sessionId === sessionToRefresh
    ) {
      if (refreshSessions) {
        refreshSessions();
      }
      setSessionToRefresh(null);
    }
  }, [chatHistory, sessionToRefresh, refreshSessions]);


  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteChatSession(conversationId);
      addNotification('Conversa excluída com sucesso!', 'success');
      refreshSessions();
      if (activeChatId === conversationId) {
        onSelectChat(null);
      }
    } catch (error) {
      addNotification('Erro ao excluir a conversa.', 'error');
      console.error("Failed to delete conversation", error);
    }
  };

  const extractMeaningfulContent = (text) => {
    const parts = text.split('\n\n');
    if (parts.length > 1 && parts[0].startsWith('Olá! Sou Quiron')) {
      return parts.slice(1).join('\n\n');
    }
    return text;
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (shareMenu.open && shareMenuRef.current && !shareMenuRef.current.contains(event.target) && !event.target.closest('[data-share-button]')) {
        setShareMenu({ open: false, messageId: null });
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFileOptions(false);
      }
    }

    if (showFileOptions || shareMenu.open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFileOptions, shareMenu]);

  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (chatHistoryRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = chatHistoryRef.current;
      const SCROLL_THRESHOLD = 50;
      const isUserNearBottom = scrollHeight - clientHeight <= scrollTop + SCROLL_THRESHOLD;

      if (isUserNearBottom || (editingMessageId && chatHistory.length > 0 && chatHistory[chatHistory.length - 1].id === editingMessageId)) {
        chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
      }
    }
  }, [chatHistory, editingMessageId]);

  useEffect(() => {
    const loadConversation = async () => {
      if (activeChatId) {
        if (chatHistory.length > 0 && chatHistory.some(m => m.sessionId === activeChatId)) {
          return;
        }
        setChatHistory([]);
        try {
          const messages = await getChatMessages(activeChatId);
          const formattedHistory = messages.map((msg, index) => ({
            sender: msg.sender,
            text: msg.content,
            fullBotResponse: msg.sender === 'bot' ? msg.content : null,
            isLoading: false,
            id: msg.id || `${activeChatId}-${index}`,
            sessionId: activeChatId,
          }));
          setChatHistory(formattedHistory);
        } catch (error) {
          addNotification(t('errorLoadingChat'), 'error');
          console.error("Erro ao carregar mensagens:", error);
        }
      } else {
        setChatHistory([]);
      }
    };
    loadConversation();
  }, [activeChatId, addNotification, t]);

  const handleSendMessage = async (messageContent, isRetry = false) => {
    const messageToSend = messageContent || message;
    if (!messageToSend.trim() && !imageFile && !textFile) {
      addNotification(t('pleaseTypeMessage'), 'warning');
      return;
    }

    let fileDetails = null;
    if (textFile) {
      fileDetails = { file: textFile, type: 'text_file' };
    } else if (imageFile) {
      fileDetails = { file: imageFile, type: 'image_file' };
    }
    const capturedImagePreviewUrl = imagePreviewUrl;

    if (!isRetry) {
      const newUserMessage = {
        sender: 'user',
        text: messageToSend,
        image: capturedImagePreviewUrl,
        isLoading: false,
        id: Date.now() + '-user',
      };
      setChatHistory(prev => [...prev, newUserMessage]);

      setMessage('');
      setTextFile(null);
      setImageFile(null);
      setImagePreviewUrl(null);
      setShowFileOptions(false);
    }

    const botMessagePlaceholder = {
      sender: 'bot',
      text: '',
      fullBotResponse: null,
      isLoading: true,
      id: Date.now() + '-bot',
    };
    setChatHistory(prev => [...prev, botMessagePlaceholder]);

    let localIsNewConversation = false;
    if (!activeChatId) {
      setIsNewConversation(true);
      localIsNewConversation = true;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const responseData = await sendChatMessage(
        messageToSend,
        isClinicalReasoningActive,
        fileDetails,
        controller.signal,
        activeChatId,
        selectedLibrary ? selectedLibrary.id : null
      );

      if (localIsNewConversation && responseData.session_id) {
        onNewChatCreated(responseData.session_id, responseData.new_title || 'Nova Conversa');
        setIsNewConversation(false);
        setSessionToRefresh(responseData.session_id);
      } else if (!localIsNewConversation && responseData.new_title) {
        onTitleUpdate(responseData.session_id, responseData.new_title);
      }

      if (fileDetails) {
        setIsClinicalReasoningActive(true);
      }

      const fullResponseText = responseData.response;
      const newSessionId = responseData.session_id;

      setChatHistory(prev =>
        prev.map(msg =>
          msg.id === botMessagePlaceholder.id
            ? { ...msg, isLoading: false, fullBotResponse: fullResponseText, text: '', sessionId: newSessionId }
            : msg
        )
      );

    } catch (err) {
      if (err.response?.data?.detail?.limit_reached === true) {
        setChatHistory(prev => prev.filter(msg => msg.id !== botMessagePlaceholder.id));
        setMessageToRetry(messageToSend);
        
        const oldestSession = sessions && sessions.length > 0 ? sessions[sessions.length - 1] : null;
        if (oldestSession) {
          setOldestSessionForDeletion(oldestSession);
          setIsLimitModalOpen(true);
        } else {
          addNotification(t('conversationNotCreatedLimitReached'), 'info');
        }
      } else if (axios.isCancel(err)) {
        console.log('Geração interrompida pelo usuário.');
        setChatHistory(prev =>
          prev.map(msg =>
            msg.isLoading && msg.sender === 'bot'
              ? { ...msg, isLoading: false, text: t('messageSendingCancelled'), fullBotResponse: t('messageSendingCancelled') }
              : msg
          )
        );
      } else {
        addNotification(err.message || t('errorSendingMessage'), 'error');
        console.error('Erro no chat:', err);
        setChatHistory(prev =>
          prev.map(msg =>
            msg.isLoading && msg.sender === 'bot'
              ? { ...msg, isLoading: false, text: t('errorLoadingResponse'), fullBotResponse: t('error') }
              : msg
          )
        );
      }
    } finally {
      abortControllerRef.current = null;
      setSelectedLibrary(null); // Limpa a biblioteca após o envio
    }
  };

  const handleConfirmDeleteOldestAndRetry = async () => {
    if (!oldestSessionForDeletion) return;
    
    setIsLimitModalOpen(false);
    await handleDeleteConversation(oldestSessionForDeletion.id);
    
    setTimeout(() => {
      handleSendMessage(messageToRetry, true); 
    }, 500);

    setOldestSessionForDeletion(null);
    setMessageToRetry('');
  };

  const handleCancelDeleteOldest = () => {
    setIsLimitModalOpen(false);
    setOldestSessionForDeletion(null);
    setMessageToRetry('');
    addNotification(t('conversationNotCreatedLimitReached'), 'info');
  };

  const handleStartEdit = (messageId, currentText) => {
    setEditingMessageId(messageId);
    setEditingText(currentText);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  const handleConfirmEdit = async () => {
    if (!editingText.trim()) {
      addNotification(t('pleaseTypeMessage'), 'warning');
      return;
    }

    const userMessageIndex = chatHistory.findIndex(msg => msg.id === editingMessageId);
    if (userMessageIndex === -1) return;

    const newChatHistory = chatHistory.slice(0, userMessageIndex + 1).map((msg, index) => {
      if (index === userMessageIndex) {
        return { ...msg, text: editingText, isLoading: false };
      }
      return msg;
    });

    const botMessagePlaceholder = {
      sender: 'bot', text: '', fullBotResponse: null, isLoading: true, id: Date.now() + '-bot',
    };
    newChatHistory.push(botMessagePlaceholder);

    setChatHistory(newChatHistory);
    const messageToSend = editingText;

    setEditingMessageId(null);
    setEditingText('');

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const responseData = await sendChatMessage(messageToSend, isClinicalReasoningActive, null, controller.signal, activeChatId);
      const fullResponseText = responseData.response;

      setChatHistory(prev =>
        prev.map(msg =>
          msg.id === botMessagePlaceholder.id
            ? { ...msg, isLoading: false, fullBotResponse: fullResponseText, text: '' }
            : msg
        )
      );
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Geração da edição interrompida pelo usuário.');
        setChatHistory(prev =>
          prev.map(msg =>
            msg.isLoading && msg.sender === 'bot'
              ? { ...msg, isLoading: false, text: t('responseInterrupted'), fullBotResponse: t('responseInterrupted') }
              : msg
          )
        );
      } else {
        addNotification(err.message || t('errorSendingMessage'), 'error');
        setChatHistory(prev =>
          prev.map(msg =>
            msg.isLoading && msg.sender === 'bot'
              ? { ...msg, isLoading: false, text: t('errorLoadingResponse'), fullBotResponse: t('error') }
              : msg
          )
        );
      }
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      return;
    }

    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
      setChatHistory(prev =>
        prev.map(msg => {
          if (msg.sender === 'bot' && !msg.isLoading && msg.fullBotResponse && msg.text.length < msg.fullBotResponse.length) {
            return { ...msg, fullBotResponse: msg.text };
          }
          return msg;
        })
      );
    }
  };

  const handleTextFileSelected = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTextFile(file);
      handleRemoveImageFile();
    }
    setShowFileOptions(false);
  };

  const handleImageExamSelected = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      setTextFile(null);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(URL.createObjectURL(file));
    }
    setShowFileOptions(false);
  };

  const handleRemoveTextFile = () => setTextFile(null);

  const handleRemoveImageFile = () => {
    setImageFile(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
  };
  
  const handleClinicalReasoningClick = () => setIsClinicalReasoningActive(!isClinicalReasoningActive);
  
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '24px';
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(scrollHeight, 150)}px`;
    }
  };

  const handleInputChange = (e) => setMessage(e.target.value);
  
  useEffect(adjustTextareaHeight, [message]);

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSendMessage(message);
    }
  };

  useEffect(() => {
    const lastBotMessage = chatHistory.find(
      (msg) =>
        msg.sender === 'bot' &&
        !msg.isLoading &&
        msg.fullBotResponse &&
        msg.text.length < msg.fullBotResponse.length
    );

    if (!lastBotMessage) {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      return;
    }

    if (typingIntervalRef.current) return;

    typingIntervalRef.current = setInterval(() => {
      setChatHistory((prevHistory) => {
        const msgIndex = prevHistory.findIndex((m) => m.id === lastBotMessage.id);
        if (msgIndex === -1) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          return prevHistory;
        }

        const newHistory = [...prevHistory];
        const currentMsg = newHistory[msgIndex];
        const currentLength = currentMsg.text.length;
        const fullLength = currentMsg.fullBotResponse.length;

        if (currentLength < fullLength) {
          const chunkSize = 5;
          const nextChunk = currentMsg.fullBotResponse.substring(currentLength, Math.min(currentLength + chunkSize, fullLength));
          newHistory[msgIndex] = { ...currentMsg, text: currentMsg.text + nextChunk };
          return newHistory;
        } else {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          newHistory[msgIndex] = { ...currentMsg, text: currentMsg.fullBotResponse };
          return newHistory;
        }
      });
    }, 20);

    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
    };
  }, [chatHistory]);
      
  const originalLeftCalculation = (isSidebarOpen ? sidebarWidth : 80) + 20 + (isSidebarOpen ? 10 : 20);
  const adjustedLeft = originalLeftCalculation - 2;

  const inputAreaWrapperStyle = {
    left: `${adjustedLeft}px`,
    right: `${20 + 8}px`,
  };

  const handleLike = async (likedMessage) => {
    const botMessageIndex = chatHistory.findIndex(m => m.id === likedMessage.id);
    const userPrompt = botMessageIndex > 0 ? chatHistory[botMessageIndex - 1].text : "N/A";

    try {
      await submitFeedback({
        feedback_type: 'like',
        content_type: 'chat_response',
        content_id: likedMessage.id,
        original_content: likedMessage.text,
        user_prompt: userPrompt,
        feedback_text: '',
        contact_permission: false,
      });
      addNotification(t('feedbackSentSuccess'), 'success');
    } catch (error) {
      console.error("Failed to submit like feedback:", error);
      addNotification(t('feedbackSentError'), 'error');
    }
  };

  const handleDislike = (dislikedMessage) => {
    const botMessageIndex = chatHistory.findIndex(m => m.id === dislikedMessage.id);
    const userPrompt = botMessageIndex > 0 ? chatHistory[botMessageIndex - 1].text : "N/A";

    setCurrentFeedback({
      type: 'dislike',
      content: dislikedMessage.text,
      id: dislikedMessage.id,
      user_prompt: userPrompt,
    });
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackSubmit = async (comment, contactPermission) => {
    try {
      await submitFeedback({
        feedback_type: currentFeedback.type,
        content_type: 'chat_response',
        content_id: currentFeedback.id,
        original_content: currentFeedback.content,
        user_prompt: currentFeedback.user_prompt,
        feedback_text: comment,
        contact_permission: contactPermission,
      });
      addNotification(t('feedbackSentSuccess'), 'success');
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      addNotification(t('feedbackSentError'), 'error');
    } finally {
      setIsFeedbackModalOpen(false);
    }
  };

  const handleCopy = (markdownText, messageId) => {
    const plainText = convertMarkdownToPlainText(markdownText);
    navigator.clipboard.writeText(plainText)
      .then(() => {
        setActionSuccess({ id: messageId, type: 'copy' });
        setTimeout(() => setActionSuccess({ id: null, type: null }), 2000);
      })
      .catch(err => {
        console.error("Erro ao copiar para a área de transferência:", err);
        addNotification(t('errorCopying'), 'error');
      });
  };

  const handleShareAction = async (shareFn, messageId) => {
    setShareMenu({ open: false, messageId: null });
    const success = await shareFn();
    if (success) {
      setActionSuccess({ id: messageId, type: 'share' });
      setTimeout(() => setActionSuccess({ id: null, type: null }), 2000);
    }
  };

  const handleRedo = async (messageId) => {
    const botMessageIndex = chatHistory.findIndex(m => m.id === messageId);
    if (botMessageIndex < 1) {
      addNotification(t('cannotRedoThisMessage'), 'warning');
      return;
    }
  
    const userMessageIndex = botMessageIndex - 1;
    const userMessage = chatHistory[userMessageIndex];
  
    if (!userMessage || userMessage.sender !== 'user') {
      addNotification(t('cannotRedoWithoutPreviousUserMessage'), 'warning');
      return;
    }
  
    const messageToSend = userMessage.text;
    const botMessagePlaceholder = {
      sender: 'bot', text: '', fullBotResponse: null, isLoading: true, id: Date.now() + '-bot',
    };
  
    const updatedHistory = [
      ...chatHistory.slice(0, botMessageIndex),
    ];
    updatedHistory.pop();
    updatedHistory.push(botMessagePlaceholder);
    
    setChatHistory(updatedHistory);
  
    const controller = new AbortController();
    abortControllerRef.current = controller;
  
    try {
      const responseData = await sendChatMessage(messageToSend, isClinicalReasoningActive, null, controller.signal, activeChatId);
      const fullResponseText = responseData.response;
  
      setChatHistory(prev =>
        prev.map(msg =>
          msg.id === botMessagePlaceholder.id
            ? { ...msg, isLoading: false, fullBotResponse: fullResponseText, text: '' }
            : msg
        )
      );
    } catch (err) {
      const errorMessage = err.response?.status === 500 
        ? t('redoFailedServerError') 
        : err.message || t('errorSendingMessage');

      addNotification(errorMessage, 'error');
      
      setChatHistory(prev =>
        prev.map(msg =>
          msg.id === botMessagePlaceholder.id
            ? { ...msg, isLoading: false, text: t('errorLoadingResponse'), fullBotResponse: t('error') }
            : msg
        )
      );
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleShare = (e, messageId) => {
    e.stopPropagation();
    setShareMenu(prev => ({
      open: prev.messageId === messageId ? !prev.open : true,
      messageId: messageId,
    }));
  };

  return (
    <div className={`${styles.chatWrapper} ${!isSidebarOpen ? styles.adjustedForClosedSidebar : ''}`}>
      <div className={styles.chatHeader}>
        <h2 className={styles.chatTitle}>{t('neuralWebTitle')}</h2>
        <ConversationNavBar
          conversations={sessions}
          activeConversation={activeChatId}
          onSelectConversation={onSelectChat}
          onNewChat={onNewChat}
          onDeleteConversation={handleDeleteConversation}
          onSessionAnimationEnd={onSessionAnimationEnd}
        />
      </div>

      <div className={styles.chatBody} ref={chatHistoryRef}>
        <div className={styles.chatHistory}>
          {chatHistory.map((msg) => (
            <div
              key={msg.id}
              className={`${styles.messageWrapper} ${msg.sender === 'user' ? styles.userMessageWrapper : styles.botMessageWrapper}`}
            >
              {msg.sender === 'user' && editingMessageId === msg.id ? (
                <div className={`${styles.messageBubble} ${styles.editingBubble}`}>
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    className={styles.editingTextarea}
                    autoFocus
                  />
                  <div className={styles.editActions}>
                    <button onClick={handleConfirmEdit} className={`${styles.editActionButton} ${styles.confirmButton}`} title={t('saveChanges')}>
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button onClick={handleCancelEdit} className={`${styles.editActionButton} ${styles.cancelButton}`} title={t('cancel')}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`${styles.messageBubble} ${msg.sender === 'user' ? styles.userMessageBubble : styles.botMessageBubble}`}
                >
                  {msg.sender === 'user' && msg.image && (
                    <div className={styles.messageImageContainer}>
                      <img src={msg.image} alt={t('imagePreview')} className={styles.messageImage} />
                    </div>
                  )}
                  {msg.isLoading && msg.sender === 'bot' ? (
                    <div className={styles.loadingAnimationContainer}>
                      <svg viewBox="0 0 75 25" className={styles.neuralWaveAnimation}>
                        <path d="M0 12.5 C 15 5 25 20 37.5 12.5 S 55 5 65 15 L 75 12.5" className={styles.neuralWavePath1} />
                        <path d="M0 12.5 C 20 20 30 5 42.5 12.5 S 60 20 70 10 L 75 12.5" className={styles.neuralWavePath2} />
                        <path d="M0 8 C 10 15 25 2 37.5 8 S 50 15 60 5 L 75 8" className={styles.neuralWavePath3} />
                      </svg>
                    </div>
                  ) : (
                    <div className={styles.messageText}>
                      <ReactMarkdown components={{ p: ({ node, ...props }) => <p style={{ margin: 0 }} {...props} /> }}>
                        {msg.sender === 'bot' ?
                          (msg.fullBotResponse && msg.text.length < msg.fullBotResponse.length ? msg.text + '▍' : msg.text) :
                          msg.text
                        }
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              )}

              {msg.sender === 'user' && !editingMessageId && !msg.isLoading && (
                <div className={styles.messageActions}>
                  <button
                    onClick={() => handleStartEdit(msg.id, msg.text)}
                    title={t('editMessage')}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                </div>
              )}

              {msg.sender === 'bot' && !msg.isLoading && msg.fullBotResponse && msg.text.length >= msg.fullBotResponse.length && (
                <div className={styles.messageActions}>
                  <button onClick={() => handleLike(msg)} title={t('like')}><FontAwesomeIcon icon={faThumbsUp} /></button>
                  <button onClick={() => handleDislike(msg)} title={t('dislike')}><FontAwesomeIcon icon={faThumbsDown} /></button>
                  <button onClick={() => handleCopy(msg.text, msg.id)} title={t('copy')}>
                    {actionSuccess.id === msg.id && actionSuccess.type === 'copy' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faCopy} />}
                  </button>
                  <button onClick={() => handleRedo(msg.id)} title={t('redoResponse')}><FontAwesomeIcon icon={faArrowRotateRight} /></button>
                  
                  <div style={{ position: 'relative' }}>
                    <button onClick={(e) => handleShare(e, msg.id)} title={t('share')} data-share-button>
                      {actionSuccess.id === msg.id && actionSuccess.type === 'share' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faShareNodes} />}
                    </button>
                    {shareMenu.open && shareMenu.messageId === msg.id && (
                      <div ref={shareMenuRef} className={`${styles.shareMenu} ${shareMenu.open && shareMenu.messageId === msg.id ? styles.shareMenuOpen : ''}`}>
                        <button className={styles.shareMenuItem} onClick={() => handleShareAction(async () => {
                          const userMessageIndex = chatHistory.findIndex(m => m.id === msg.id) - 1;
                          const title = userMessageIndex >= 0 ? chatHistory[userMessageIndex].text : t('quiron');
                          const content = extractMeaningfulContent(msg.text);
                          return handleShareAsTxt(content, title, t, addNotification);
                        }, msg.id)}>
                          <FontAwesomeIcon icon={faFileLines} />
                          <span>{t('shareAsTxt')}</span>
                        </button>
                        <button className={styles.shareMenuItem} onClick={() => handleShareAction(async () => {
                          const content = extractMeaningfulContent(msg.text);
                          return handleShareAsPdf(content, addNotification, i18n);
                        }, msg.id)}>
                          <FontAwesomeIcon icon={faFilePdf} />
                          <span>{t('shareAsPdf')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div
        className={styles.inputAreaFixedWrapper}
        style={inputAreaWrapperStyle}
      >
        {selectedLibrary && (
          <div className={styles.libraryTag}>
            <FontAwesomeIcon icon={faBook} />
            <span>{t('libraryAttached')}: {selectedLibrary.name}</span>
            <button onClick={() => setSelectedLibrary(null)} title={t('removeLibrary')}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        )}
        <div className={styles.chatInputContainer}>
          {textFile && (
            <div className={styles.filePreviewCard}>
              <FontAwesomeIcon icon={faFileLines} className={styles.previewIcon} />
              <div className={styles.previewDetails}>
                <span className={styles.previewFileName}>{textFile.name}</span>
                <span className={styles.previewFileType}>{t('textFile')}</span>
              </div>
              <button onClick={handleRemoveTextFile} className={styles.previewRemoveButton}>×</button>
            </div>
          )}
          {imageFile && imagePreviewUrl && (
            <div className={styles.filePreviewCard}>
              <img src={imagePreviewUrl} alt={t('imagePreview')} className={styles.previewImage} />
              <div className={styles.previewDetails}>
                <span className={styles.previewFileName}>{imageFile.name}</span>
                <span className={styles.previewFileType}>{t('imageFile')}</span>
              </div>
              <button onClick={handleRemoveImageFile} className={styles.previewRemoveButton}>×</button>
            </div>
          )}
          <div className={styles.inputArea}>
            <div className={styles.textareaWrapper}>
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleInputChange}
                onKeyUp={handleKeyUp}
                placeholder={t('chatPlaceholder')}
                rows={1}
                disabled={chatHistory.some(m => m.isLoading)}
              />
            </div>
            <div className={styles.chatInputButtonsWrapper}>
              <button
                className={`${styles.clinicalReasoningButton} ${isClinicalReasoningActive ? styles.active : ''}`}
                onClick={handleClinicalReasoningClick}
                title={t('toggleClinicalReasoning')}
              >
                {t('clinicalReasoning')}
              </button>
              <div className={styles.fileUploadDropdown} ref={dropdownRef}>
                <button
                  className={`${styles.chatIconButton} ${styles.fileUploadButton} ${(showFileOptions || textFile || imageFile || selectedLibrary) ? styles.active : ''}`}
                  title={t('attachFile')}
                  onClick={() => setShowFileOptions(!showFileOptions)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.49" />
                  </svg>
                </button>
                {showFileOptions && (
                  <div className={styles.dropdownContent}>
                    <button
                      onClick={() => {
                        document.getElementById('textFile-input').click();
                        setShowFileOptions(false);
                      }}
                    >
                      {/* SUBSTITUA os botões antigos por estes */}
                      <FontAwesomeIcon icon={faFileLines} className={styles.dropdownIcon} />
                      <span>{t('textFile')}</span>
                    </button>
                    <button
                      onClick={() => {
                        document.getElementById('imageExam-input').click();
                        setShowFileOptions(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faImage} className={styles.dropdownIcon} />
                      <span>{t('imageFile')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setIsLibraryModalOpen(true);
                        setShowFileOptions(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faBook} className={styles.dropdownIcon} /> {/* LINHA ADICIONADA */}
                      <span>{t('selectLibrary')}</span>
                    </button>
                  </div>
                )}
                <input type="file" id="textFile-input" style={{ display: 'none' }} onChange={handleTextFileSelected} accept=".txt,.pdf,.docx,.md" />
                <input type="file" id="imageExam-input" style={{ display: 'none' }} onChange={handleImageExamSelected} accept="image/png,image/jpeg,image/gif,image/webp" />
              </div>
              {chatHistory.some(m => m.isLoading || (m.sender === 'bot' && m.fullBotResponse && m.text.length < m.fullBotResponse.length)) ? (
                <button
                  onClick={handleStopGeneration}
                  className={`${styles.chatIconButton} ${styles.stopButton}`}
                  title={t('stopGeneration')}
                >
                  <FontAwesomeIcon icon={faStop} />
                </button>
              ) : (
                <button
                  onClick={() => handleSendMessage(message)}
                  disabled={!message.trim() && !imageFile && !textFile}
                  className={`${styles.chatIconButton} ${styles.sendButton}`}
                  title={t('sendMessageHint')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.chatInfoText}>{t('bibliographicalReferencesHealthNote')}</div>
      </div>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackType={currentFeedback.type}
      />
      <ConfirmationModal
        isOpen={isLimitModalOpen}
        onClose={handleCancelDeleteOldest}
        onConfirm={handleConfirmDeleteOldestAndRetry}
        title={t('conversationLimitReachedTitle')}
        message={t('conversationLimitReachedMessage', { 
          limit: 20, 
          title: oldestSessionForDeletion?.title 
        })}
        confirmButtonText={t('deleteOldestAndContinue')}
        cancelButtonText={t('cancel')}
      />
      <LibrarySelectionModal
        isOpen={isLibraryModalOpen}
        onClose={() => setIsLibraryModalOpen(false)}
        onSelectLibrary={(library) => setSelectedLibrary(library)}
      />
    </div>
  );
}

export default Chat;